import type { ParsedGame } from "@onchainreps/chess";
import type { LocalStore, StoredDrill, StoredGame } from "@onchainreps/storage";
import type { GameReport, MoveReview, PositionAnalysis } from "./analysis";

export interface ImportMeta {
  source: "lichess" | "chesscom" | "manual" | "pgn";
  player?: string | null;
}

function fingerprint(text: string): string {
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(36);
}

export function gameFingerprint(game: ParsedGame): string {
  const key = [
    game.headers.White ?? "",
    game.headers.Black ?? "",
    game.headers.Date ?? "",
    game.headers.UTCDate ?? game.headers.Date ?? "",
    game.result,
    game.moves.length,
    game.moves.map((move) => move.san).join(" "),
  ].join("|");
  return `g_${fingerprint(key)}`;
}

function sideForPlayer(
  game: ParsedGame,
  player: string | null | undefined,
): "white" | "black" | null {
  if (!player) return null;
  const needle = player.trim().toLowerCase();
  if (!needle) return null;
  const white = (game.headers.White ?? "").trim().toLowerCase();
  const black = (game.headers.Black ?? "").trim().toLowerCase();
  if (white === needle) return "white";
  if (black === needle) return "black";
  if (white.includes(needle)) return "white";
  if (black.includes(needle)) return "black";
  return null;
}

export function buildDrills(
  game: ParsedGame,
  positions: PositionAnalysis[],
  reviews: MoveReview[],
  gameId: string,
): StoredDrill[] {
  const drills: StoredDrill[] = [];

  for (const review of reviews) {
    if (review.category !== "blunder" && review.category !== "miss" && review.category !== "inaccuracy") {
      continue;
    }
    const position = positions[review.ply];
    const move = game.moves[review.ply];
    if (!position?.bestMove || !move) continue;
    if (position.bestMove === move.uci) continue;

    drills.push({
      id: `${gameId}:${review.ply}`,
      gameId,
      ply: review.ply,
      fen: position.fen,
      bestMove: position.bestMove,
      category: review.category,
      loss: Math.round(review.loss * 10) / 10,
      side: move.color,
      createdAt: new Date().toISOString(),
    });
  }

  return drills;
}

export async function persistAnalysis(
  store: LocalStore,
  game: ParsedGame,
  positions: PositionAnalysis[],
  reviews: MoveReview[],
  report: GameReport,
  meta: ImportMeta,
): Promise<void> {
  const id = gameFingerprint(game);
  const existing = await store.getGame(id);

  let blundersWhite = 0;
  let blundersBlack = 0;
  for (const review of reviews) {
    if (review.category !== "blunder") continue;
    const move = game.moves[review.ply];
    if (!move) continue;
    if (move.color === "white") blundersWhite += 1;
    else blundersBlack += 1;
  }

  const stored: StoredGame = {
    id,
    source: meta.source,
    pgn: game.pgn,
    white: game.headers.White ?? "White",
    black: game.headers.Black ?? "Black",
    result: game.result,
    opening: game.opening ?? game.headers.Opening ?? null,
    eco: game.eco ?? game.headers.ECO ?? null,
    playedAt: game.headers.UTCDate ?? game.headers.Date ?? null,
    importedAt: existing?.importedAt ?? new Date().toISOString(),
    accuracyWhite: Math.round(report.accuracyWhite * 10) / 10,
    accuracyBlack: Math.round(report.accuracyBlack * 10) / 10,
    blundersWhite,
    blundersBlack,
    playerColor: sideForPlayer(game, meta.player),
  };

  await store.saveGames([stored]);
  await store.deleteDrillsForGame(id);
  await store.saveDrills(buildDrills(game, positions, reviews, id));
}
